<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAppStore } from '@/stores/appStore'
import { useAuthStore } from '@/stores/authStore'
import { updates } from '@/data/updates'
import UpdateContent from './UpdateContent.vue'

const appStore = useAppStore()
const authStore = useAuthStore()

const showDialog = ref(false)
const hasNewUpdates = computed(() => {
  const lastUpdate = new Date(Math.max(...updates.map((u) => u.date.getTime())))
  return lastUpdate > appStore.lastViewedUpdates
})

const newUpdatesCount = computed(() => {
  return updates.filter((update) => update.date > appStore.lastViewedUpdates).length
})

const newUpdates = computed(() => {
  return updates
    .filter((update) => update.date > appStore.lastViewedUpdates)
    .sort((a, b) => b.date.getTime() - a.date.getTime()) // Sort by date, newest first
})

function openDialog() {
  showDialog.value = true
}

function closeDialog() {
  showDialog.value = false
  appStore.updateLastViewedUpdates(new Date())
}

function formatDate(date: Date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>

<template>
  <div v-if="authStore.isAuthenticated">
    <div v-if="hasNewUpdates" class="whats-new-container">
      <q-btn round color="primary" icon="mdi-gift-outline" class="whats-new-fab" @click="openDialog">
        <q-tooltip>What's New</q-tooltip>
      </q-btn>
      <div class="badge-container">
        <q-badge color="negative" class="update-badge">{{ newUpdatesCount }}</q-badge>
      </div>
    </div>

    <q-dialog v-model="showDialog" persistent>
      <q-card class="whats-new-card">
        <q-card-section class="row items-center">
          <div class="text-h4 text-secondary">What's New</div>
          <q-space />
          <q-btn icon="mdi-close" flat round dense v-close-popup @click="closeDialog" />
        </q-card-section>

        <q-card-section class="q-py-none" v-for="(update, index) in newUpdates" :key="index">
          <div class="update-item">
            <div class="text-h6 q-mb-sm text-primary">
              v{{ update.version }} <span class="text-caption text-grey-6 q-mb-sm">{{ formatDate(update.date) }}</span>
            </div>
            <div class="text-h5 q-mb-sm text-secondary">{{ update.title }}</div>
            <UpdateContent :content="update.update" />
          </div>
          <q-separator v-if="index < newUpdates.length - 1" class="q-my-md" />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Close" color="primary" v-close-popup @click="closeDialog" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style scoped>
.whats-new-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.whats-new-fab {
  position: relative;
  width: 56px;
  height: 56px;
}

.badge-container {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 1001;
}

.update-badge {
  font-size: 12px;
  padding: 4px 6px;
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.update-item {
  background-color: black;
  border-radius: 8px;
  padding: 15px;
}

.update-content {
  font-family: Arial, sans-serif;
  line-height: 1.6;
}
.update-content h6 {
  color: #4767e1;
  margin-bottom: 10px;
}

.update-content ul {
  margin-top: 10px;
  margin-bottom: 10px;
}

.update-content li {
  margin-bottom: 8px;
  color: white;
}

.whats-new-card {
  width: 100%;
  max-width: 600px;
  margin: 16px;
}

@media (max-width: 600px) {
  .whats-new-card {
    margin: 8px;
  }
}

:deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
</style>
